import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { DuplicateQueryModal } from '@/sections/insights/queryBuilder/components/Modals';
import {
  QueryWriter,
  QueryWriterHandle,
} from '@/sections/insights/queryBuilder/components/QueryDisplay/QueryWriter';
import { QueryResults } from '@/sections/insights/queryBuilder/components/QueryResults';
import { useQueryOperations } from '@/sections/insights/queryBuilder/hooks/useQueryOperations';
import { useSaveQuery } from '@/sections/insights/queryBuilder/hooks/useSaveQuery';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { EmptyState } from './EmptyState';
import { QuerySplitLayout } from './QuerySplitLayout';
import { ActionsToolbar } from './QuerySplitVisualizer/ActionsToolbar';

/**
 * QuerySplitWriter - JSON editor with split-pane results view
 *
 * Layout:
 * - Left pane: QueryWriter (pure JSON editor)
 * - Right pane: EmptyState initially, QueryResults after execution
 *
 * Behavior:
 * - Matches QuerySplitEditor pattern: EmptyState → QueryResults
 * - Uses QuerySplitLayout for consistent split-pane behavior
 * - Storage key: "querySplitWriterRatio"
 *
 * Triggered by:
 * - QueryMenu "Edit Query (JSON)" action
 * - QueryBuilderWorkstation when mode === 'split-writer'
 */
export const QuerySplitWriter = () => {
  const { queryState, isGlobal, getSavedQuery } = useQueryBuilder();
  const [showResults, setShowResults] = useState(false);

  // Cache the query snapshot from last Execute click
  const [executedQuery, setExecutedQuery] = useState<SavedQuery | null>(null);

  // Ref to QueryWriter for programmatic execution
  const queryWriterRef = useRef<QueryWriterHandle>(null);

  // Add action handlers
  const { isSaving } = useQueryOperations();
  const { save } = useSaveQuery();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const handleDuplicate = () => {
    setIsDuplicateModalOpen(true);
  };

  const handleExecute = () => {
    // Step 1: Trigger QueryWriter to parse/validate/dispatch and get parsed query
    const parsedQuery = queryWriterRef.current?.parseAndExecute();

    // Step 2: Build snapshot from parsed query (synchronous - no race condition)
    if (parsedQuery && queryState) {
      const snapshot: SavedQuery = {
        ...queryState.metadata,
        query: parsedQuery,
      };
      setExecutedQuery(snapshot);
      setShowResults(true);
    }
  };

  const handleSave = async () => {
    try {
      // Step 1: Trigger QueryWriter to parse/validate/dispatch and get parsed query
      const parsedQuery = queryWriterRef.current?.parseAndExecute();

      // Step 2: Build SavedQuery from parsed query with latest JSON
      if (parsedQuery && queryState) {
        const updatedQuery: SavedQuery = {
          ...queryState.metadata,
          query: parsedQuery,
          folderId: 'custom-queries' as const,
        };

        await save(updatedQuery);
      }
    } catch (error) {
      console.error('Failed to save query:', error);
      toast.error('Failed to save query');
    }
  };

  // Reset cache when switching to different query (React best practice pattern)
  const [prevQueryId, setPrevQueryId] = useState(queryState?.metadata.id);
  if (queryState?.metadata.id !== prevQueryId) {
    setPrevQueryId(queryState?.metadata.id);
    setExecutedQuery(null);
    setShowResults(false);
  }

  // Error handling for missing query
  if (!queryState) {
    toast.error('No query selected');
    return null;
  }

  return (
    <>
      <QuerySplitLayout
        leftContent={
          <>
            <ActionsToolbar
              editable={true}
              isGlobal={isGlobal}
              onExecute={handleExecute}
              onDuplicate={handleDuplicate}
              onSave={handleSave}
              isSaving={isSaving}
            />
            <div className="relative h-full">
              <QueryWriter ref={queryWriterRef} />
            </div>
          </>
        }
        rightContent={
          showResults ? (
            <QueryResults selectedQuery={executedQuery} />
          ) : (
            <EmptyState />
          )
        }
        storageKey="querySplitWriterRatio"
        testId="query-split-writer"
      />

      {/* Duplicate Modal */}
      {getSavedQuery() && (
        <DuplicateQueryModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          originalQuery={getSavedQuery()!}
        />
      )}
    </>
  );
};
